import { ReactNode, useEffect, useRef, useState } from "react";
import SlickSlider from "react-slick";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useHovered } from "../../hooks/useHovered";

export const ImageSlider = ({
  images,
  dotsSide = "right",
  showDots = true,
  includeThumbnails,
  className,
  imageClassName,
  dotsClassName,
  thumbnailsClassName,
  thumbnailClassName,
  activeThumbnailClassName,
  hoverAnimate,
  swipe = true,
}: {
  images: (string | null)[];
  dotsSide?: "right" | "left";
  showDots?: boolean;
  includeThumbnails?: boolean;
  className?: string;
  imageClassName?: string;
  dotsClassName?: string;
  thumbnailsClassName?: string;
  thumbnailClassName?: string;
  activeThumbnailClassName?: string;
  hoverAnimate?: boolean;
  swipe?: boolean;
}) => {
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderRef = useRef<SlickSlider>(null);
  const hoverRef = useRef(null);
  const hovered = useHovered(hoverRef) && hoverAnimate && swipe;
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const showThumbnails = includeThumbnails && images.length > 1;
  let settings = {
    dots: showDots && (hovered || !hoverAnimate) && images.length > 1,
    arrows: false,
    infinite: !!hoverAnimate,
    swipe,
    touchMove: swipe,
    speed: 500,
    autoplaySpeed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    dotsClass: twMerge("absolute right-2 bottom-2 left-2", dotsClassName),
    beforeChange: (current: number, next: number) => setSliderIndex(next),
    appendDots: (dots: ReactNode) => (
      <div>
        <ul
          className={twMerge(
            "flex items-center justify-center gap-1",
            dotsSide === "right" ? "sm:justify-end" : "sm:justify-start",
          )}
        >
          {dots}
        </ul>
      </div>
    ),
    customPaging: (i: number) => (
      <span
        className={twMerge(
          "block h-[10px] w-[10px] rounded-full border border-zinc-500 bg-zinc-400 transition-all",
          sliderIndex === i ? "w-[28px] border-zinc-300 bg-white" : "w-[10px]",
        )}
      ></span>
    ),
  };

  /**
   * Functionality to auto scroll on hover
   * Using Slick play functionality is hit and miss with hovered state
   */

  // Refs to ensure same value used throughout the interval
  const timer = useRef<number | null>(null);
  const sliderIndexRef = useRef(sliderIndex);

  // Set ref for current index to be read in interval
  useEffect(() => {
    sliderIndexRef.current = sliderIndex;
  }, [sliderIndex]);

  useEffect(() => {
    if (hovered && !timer.current) {
      timer.current = window.setInterval(() => {
        let index =
          sliderIndexRef.current < images.length - 1
            ? sliderIndexRef.current + 1
            : 0;
        setSliderIndex(index);
        sliderRef.current?.slickGoTo(index);
      }, 2000);
    } else if (!hovered && timer.current) {
      window.clearInterval(timer.current);
    }

    return () => {
      if (timer.current) {
        window.clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [hovered, images.length]);

  return (
    <div ref={hoverRef}>
      <SlickSlider
        ref={sliderRef}
        {...settings}
        className={twMerge(
          "max-w-screen leading-[0]",
          className,
          !swipe && "pointer-events-none",
        )}
      >
        {images.map((image, idx) => (
          <div
            key={idx}
            className={twMerge(
              "relative h-full w-full overflow-hidden pt-[56.25%]",
              !imageClassName && "rounded-lg",
              imageClassName,
            )}
          >
            <img
              alt={"slider image"}
              src={`${image}?w=1200&auto=compress`}
              className={twMerge(
                "absolute inset-0 block h-full w-full object-cover",
                hoverAnimate && "transition-transform duration-500",
                hovered && "scale-110",
              )}
            />
          </div>
        ))}
      </SlickSlider>
      {isDesktop && showThumbnails && (
        <div
          className={twMerge(
            "flex w-full justify-end gap-2 pt-2",
            thumbnailsClassName,
          )}
        >
          {images.map((image, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Show product image ${idx + 1}`}
              aria-pressed={sliderIndex === idx}
              className={twMerge(
                "relative h-12 w-12 shrink-0 cursor-pointer",
                thumbnailClassName,
                sliderIndex === idx && activeThumbnailClassName,
              )}
              onClick={() => sliderRef.current?.slickGoTo(idx)}
            >
              <img
                alt={"slider image"}
                src={`${image}?w=96&h=96&auto=compress&fit=crop`}
                className={
                  "absolute inset-0 block h-full w-full rounded-lg object-cover"
                }
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
